import React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";

export const TestimonialCard = ({testimonial, featured = false}) => {
    if (!testimonial) return null;

    const {title, quote, author, role, organization} = testimonial;

    return (
        <Card
            elevation={0}
            component="figure"
            sx={{
                m: 0,
                height: "100%",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                border: "1px solid",
                borderColor: featured ? "rgba(40, 82, 56, 0.22)" : "rgba(40, 82, 56, 0.12)",
                borderRadius: 4,
                bgcolor: featured ? "rgba(40, 82, 56, 0.03)" : "background.paper",
                p: {xs: 2, sm: 3.5},
                position: "relative",
                overflow: "hidden",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 12px 32px rgba(40, 82, 56, 0.08)",
                    borderColor: "primary.main",
                },
            }}
        >
            {/* Background Quote Watermark */}
            <FormatQuoteIcon
                aria-hidden="true"
                sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    fontSize: 72,
                    color: "primary.main",
                    opacity: 0.08,
                    pointerEvents: "none",
                }}
            />

            <CardContent
                sx={{
                    p: 0,
                    "&:last-child": {pb: 0},
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    zIndex: 1,
                }}
            >
                {title && (
                    <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                            fontWeight: 700,
                            color: "primary.main",
                            fontSize: {xs: "1.05rem", sm: "1.15rem"},
                            lineHeight: 1.35,
                            mb: 2,
                        }}
                    >
                        {title}
                    </Typography>
                )}

                <Box
                    component="blockquote"
                    sx={{
                        m: 0,
                        mb: 3,
                        flexGrow: 1,
                        color: "text.primary",
                        fontSize: "0.95rem",
                        lineHeight: 1.7,
                        fontStyle: "italic",
                    }}
                >
                    &laquo;{quote}&raquo;
                </Box>

                <Box
                    component="figcaption"
                    sx={{
                        mt: "auto",
                        pt: 2,
                        borderTop: "1px solid rgba(40, 82, 56, 0.08)",
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 700,
                            color: "text.primary",
                            fontSize: "0.95rem",
                        }}
                    >
                        {author}
                    </Typography>

                    {role && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: "text.secondary",
                                fontSize: "0.825rem",
                                lineHeight: 1.4,
                            }}
                        >
                            {role}
                        </Typography>
                    )}

                    {organization && (
                        <Box sx={{mt: 0.75, display: "flex", alignItems: "center", gap: 0.5}}>
                            <Chip
                                icon={<LocationOnOutlinedIcon sx={{fontSize: "0.9rem !important", color: "primary.main"}} />}
                                label={organization}
                                size="small"
                                variant="outlined"
                                sx={{
                                    borderColor: "rgba(40, 82, 56, 0.2)",
                                    color: "primary.main",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    bgcolor: "rgba(40, 82, 56, 0.04)",
                                    height: 24,
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};
